<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="price-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("PRICING.LIST") }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="hasPermissions('pricing-create')"
          class="mr-4 warning"
          :disabled="isProcessing"
          :loading="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="pricings-table"
              height="500"
              name="pricings-table"
              :headers="headers"
              :hidden-headers="hiddenHeaders"
              :filters="filters"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{
                method: tableItemsMethod
              }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template
                v-for="(header, index) in columnHeaders"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <TableRowActions
                  v-if="header.value === 'options'"
                  :key="index"
                  :show-delete="false"
                >
                  <template #view>
                    <v-btn
                      v-if="hasPermissions('pricing-create')"
                      icon
                      color="indigo"
                      @click="handleCloneClick(item.item.id)"
                    >
                      <v-tooltip top color="primary">
                        <template #activator="{ on, attrs }">
                          <v-icon dark v-bind="attrs" v-on="on">
                            mdi-content-copy
                          </v-icon>
                        </template>
                        <span>{{ $t("PRICING.CLONE") }}</span>
                      </v-tooltip>
                    </v-btn>
                    <v-btn
                      icon
                      color="indigo"
                      @click="handlePrintView(item.item.id)"
                    >
                      <v-tooltip top color="primary">
                        <template #activator="{ on, attrs }">
                          <v-icon dark v-bind="attrs" v-on="on">
                            mdi-printer
                          </v-icon>
                        </template>
                        <span>{{ $t("GENERAL.LABELS.PRINT_VIEW") }}</span>
                      </v-tooltip>
                    </v-btn>
                    <v-btn
                      icon
                      color="indigo"
                      @click="handleDownload(item.item)"
                    >
                      <v-tooltip top color="primary">
                        <template #activator="{ on, attrs }">
                          <v-icon dark v-bind="attrs" v-on="on">
                            mdi-download
                          </v-icon>
                        </template>
                        <span>{{ $t("GENERAL.LABELS.DOWNLOAD") }}</span>
                      </v-tooltip>
                    </v-btn>
                  </template>
                  <template #edit>
                    <v-btn
                      v-if="hasPermissions('pricing-edit')"
                      icon
                      color="primary"
                      @click="handleEditClick(item.item.id)"
                    >
                      <v-icon dark>
                        mdi-pencil
                      </v-icon>
                    </v-btn>
                  </template>
                </TableRowActions>
                <template v-else-if="header.value === 'is_active'">
                  <v-switch
                    :input-value="item.value"
                    :true-value="true"
                    :false-value="false"
                    hide-details
                    dense
                    class="mt-0 pt-0"
                    :disabled="!hasPermissions('pricing-edit') || isProcessing"
                    @change="handleActiveChange(item.item, $event)"
                  ></v-switch>
                </template>
                <template v-else>
                  {{ item.value ?? "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <HtmlEditor
      ref="htmlPreview"
      :hideButton="'true'"
      :getContent="getContent"
    ></HtmlEditor>
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import TableRowActions from "@/views/partials/v-table/components/TableRowActions.vue";
import HtmlEditor from "@/views/partials/content/HtmlEditor.vue";
import PricingService from "@/common/pricing.service.js";
import userStateService from "@/common/userState.service.js";
import { SET_BREADCRUMB } from "@/store/breadcrumbs.module";
import { GET_ORDERED_MAPPING_VALUES } from "@/store/mappingValues.module";
import {
  GET_PRICINGS,
  GET_PRICING,
  GET_TAG_PRICING,
  UPDATE_ACTIVE_PRICE,
} from "@/store/pricing.module";

export default {
  name: "ShowPricing",
  components: {
    Table,
    TableRowActions,
    HtmlEditor,
  },
  data() {
    return {
      tableName: "pricings-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      tableItemsMethod: GET_PRICINGS,
      content: "",
      headers: [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("PRICING.ACTIVE"),
          value: "is_active",
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("GENERAL.LABELS.NAME"),
          value: "name",
          align: "center",
          width: "22%",
        },
        {
          text: this.$t("PRICING.CREATED_BY"),
          value: "user_name",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("GENERAL.LABELS.CREATED_DATE"),
          value: "created_at",
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "18%",
        },
      ],
      hiddenHeaders: [
        {
          text: this.$t("PRICING.EXPIRE_DATE"),
          value: "expire_date",
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("PRICING.PUBLISHED_BY"),
          value: "published_name",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("PRICING.PUBLISHED_DATE"),
          value: "published_date",
          align: "center",
          width: "12%",
        },
      ],
      filters: [
        {
          key: "is_active",
          label: this.$t("PRICING.ACTIVE"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [
            { id: 1, label: this.$t("GENERAL.LABELS.YES") },
            { id: 0, label: this.$t("GENERAL.LABELS.NO") },
          ],
        },
      ],
    };
  },
  computed: {
    columnHeaders() {
      return [...this.headers, ...this.hiddenHeaders];
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: this.$t("PRICING.LIST") },
    ]);

    this.$store.dispatch(GET_ORDERED_MAPPING_VALUES, 5);

    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );

    const selectedHiddenHeadersKey = `table-state.${this.tableName}.selectedHiddenHeaders`;
    const selectedHiddenHeadersInitializedKey = `table-state.${this.tableName}.selectedHiddenHeadersInitialized`;

    if (!userStateService.getState(selectedHiddenHeadersInitializedKey, false)) {
      userStateService.setState(selectedHiddenHeadersKey, [...this.hiddenHeaders]);
      userStateService.setState(selectedHiddenHeadersInitializedKey, true);
    }

    this.userStatePrepared = true;
  },
  methods: {
    handleCreateClick() {
      this.$router.push({ name: "create-pricing" });
    },
    handleEditClick(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );

      this.$router.push({ name: "edit-pricing", params: { id } });
    },
    handleCloneClick(id) {
      this.$router.push({
        name: "create-pricing",
        params: { clone_id: id },
      });
    },
    handleActiveChange(row, value) {
      this.isProcessing = true;

      this.$store
        .dispatch(UPDATE_ACTIVE_PRICE, {
          id: row.id,
          params: {
            is_active: value ? 1 : 0,
          },
        })
        .then(() => {
          row.is_active = !!value;
        })
        .catch(() => {
          this.$refs["pricings-table"]?.fetchServerData();
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    handlePrintView(id) {
      this.isProcessing = true;

      this.$store
        .dispatch(GET_PRICING, id)
        .then((response) => {
          return this.$store.dispatch(GET_TAG_PRICING, response).then((r) => {
            this.content = PricingService.getContent(r.prepared);
            this.$refs.htmlPreview.showDialog();
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    handleDownload(row) {
      this.isProcessing = true;

      this.$store
        .dispatch(GET_PRICING, row.id)
        .then((response) => {
          return this.$store.dispatch(GET_TAG_PRICING, response).then((r) => {
            PricingService.download(r.prepared, response.name);
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    getContent() {
      return this.content;
    },
  },
};
</script>
